import { useMemo } from 'react';
import { t } from '../i18n/index.js';

const pad = (n: number) => String(n).padStart(2, '0');

/**
 * A time picker that reads as 12-hour with AM/PM and stores 24-hour.
 *
 * Three selects rather than a native time input, so the display never follows the browser's
 * locale — a native input shows AM/PM in some locales and 24-hour in others, and an office
 * setting should not depend on which laptop it is being edited from. The value handed in and
 * out is always 'HH:MM' on a 24-hour clock; only this control's face is 12-hour. Times shown
 * anywhere else in the apps and in the reports stay 24-hour.
 *
 * Minutes step by five; a value set elsewhere that is not on the step is kept as an extra
 * option rather than being silently rounded.
 */
export function TimeField({
  value,
  onChange,
  label,
  id,
}: {
  value: string;
  onChange: (hhmm: string) => void;
  label?: string;
  id?: string;
}) {
  const [hh = '00', mm = '00'] = value.split(':');
  const hour24 = Number(hh);
  const meridiem: 'AM' | 'PM' = hour24 >= 12 ? 'PM' : 'AM';
  // 00:xx reads as 12 AM and 12:xx as 12 PM, the way a clock face does.
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;

  const minutes = useMemo(() => {
    const steps = Array.from({ length: 12 }, (_, i) => pad(i * 5));
    return steps.includes(mm) ? steps : [...steps, mm].sort();
  }, [mm]);

  /** 12-hour face back to the 24-hour value that is stored. */
  const to24 = (h12: number, ap: 'AM' | 'PM') => (ap === 'AM' ? h12 % 12 : (h12 % 12) + 12);

  return (
    <span className="time-field" role="group" aria-label={label}>
      <select
        id={id}
        aria-label={label ? `${label} — ${t('time_hours')}` : t('time_hours')}
        value={String(hour12)}
        onChange={(e) => onChange(`${pad(to24(Number(e.target.value), meridiem))}:${mm}`)}
      >
        {Array.from({ length: 12 }, (_, i) => i + 1).map((h) => (
          <option key={h} value={h}>
            {h}
          </option>
        ))}
      </select>
      <span aria-hidden="true">:</span>
      <select
        aria-label={label ? `${label} — ${t('time_minutes')}` : t('time_minutes')}
        value={mm}
        onChange={(e) => onChange(`${hh}:${e.target.value}`)}
      >
        {minutes.map((m) => (
          <option key={m} value={m}>
            {m}
          </option>
        ))}
      </select>
      <select
        aria-label={label ? `${label} — ${t('time_meridiem')}` : t('time_meridiem')}
        value={meridiem}
        onChange={(e) =>
          onChange(`${pad(to24(hour12, e.target.value as 'AM' | 'PM'))}:${mm}`)
        }
      >
        <option value="AM">{t('time_am')}</option>
        <option value="PM">{t('time_pm')}</option>
      </select>
    </span>
  );
}
